import { trackSponsorClick } from "../lib/analytics";
import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Instagram, Facebook, Mail, MapPin, Phone } from "lucide-react";
import { getSiteSettings, listSponsors } from "../lib/db";

export default function Footer() {
  const [settings, setSettings] = useState({});
  const [sponsors, setSponsors] = useState([]);
  const location = useLocation();

  useEffect(() => {
    getSiteSettings().then(setSettings).catch(() => {});
    listSponsors().then(setSponsors).catch(() => {});
  }, []);

  const showSponsors = sponsors.length > 0 && location.pathname !== "/contatti";

  const getTierClass = (tier) => {
    if (tier === "oro") return "h-64";
    if (tier === "argento") return "h-52";
    return "h-40";
  };

  const getImgClass = (tier) => {
    if (tier === "oro") return "max-h-16 max-w-[200px]";
    if (tier === "argento") return "max-h-12 max-w-[140px]";
    return "max-h-10 max-w-[110px]";
  };

  const getTextClass = (tier) => {
    if (tier === "oro") return "text-xl";
    if (tier === "argento") return "text-base";
    return "text-sm";
  };

  return (
    <footer className="border-t border-zinc-900 bg-zinc-950 mt-24" data-testid="site-footer">

      {showSponsors && (
        <div className="border-b border-zinc-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
            <p className="text-overline text-yellow-400 text-xs font-display font-bold uppercase tracking-wider mb-6 text-center">
              I nostri sponsor
            </p>
            <div className="flex flex-wrap items-start justify-center gap-6">
              {sponsors.map((s) => (
                <div key={s.id} className="flex flex-col items-center gap-1.5">
                  <a
                    href={s.website_url || "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={"group flex items-center justify-center aspect-square rounded-xl border border-zinc-800 bg-zinc-900 hover:border-yellow-400/50 transition-colors " + getTierClass(s.tier)}
                    title={s.name}
                    onClick={() => trackSponsorClick(s.name, s.tier)}
                  >
                    {s.logo_url ? (
                      <img
                        src={s.logo_url}
                        alt={s.name}
                        className="w-full h-full object-contain p-2 opacity-70 group-hover:opacity-100 transition-opacity"
                      />
                    ) : (
                      <span className={"font-display font-bold text-zinc-400 group-hover:text-yellow-400 transition-colors " + getTextClass(s.tier)}>
                        {s.name}
                      </span>
                    )}
                  </a>
                  {s.telefono && (
                    <a
                      href={`tel:${s.telefono.replace(/\s+/g, "")}`}
                      className="flex items-center gap-1 text-[11px] text-zinc-500 hover:text-yellow-400 transition-colors"
                    >
                      <Phone className="h-3 w-3" /> {s.telefono}
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div className="md:col-span-2">
          <div className="flex items-center gap-2.5 mb-4">
            <div className="h-9 w-9 rounded-lg bg-yellow-400 flex items-center justify-center">
              <span className="font-display font-black text-zinc-950 text-lg leading-none">R</span>
            </div>
            <div className="leading-tight">
              <div className="font-display font-black text-sm tracking-tight uppercase">{settings.nome_societa || "ASD Rubiana"}</div>
              <div className="font-display text-[10px] tracking-[0.25em] uppercase text-yellow-400">Calcio • Palestra</div>
            </div>
          </div>
          <p className="text-sm text-zinc-400 max-w-md mb-6 leading-relaxed">
            Una società sportiva nel cuore della Val di Susa. Squadra di calcio per
            tutte le età e una palestra moderna per la community di Rubiana.
          </p>
          <div className="flex gap-3">
            <a href={settings.instagram || "#"} className="h-10 w-10 rounded-full border border-zinc-800 hover:border-yellow-400 hover:text-yellow-400 flex items-center justify-center transition-colors" data-testid="footer-instagram">
              <Instagram className="h-4 w-4" />
            </a>
            <a href={settings.facebook || "#"} className="h-10 w-10 rounded-full border border-zinc-800 hover:border-yellow-400 hover:text-yellow-400 flex items-center justify-center transition-colors" data-testid="footer-facebook">
              <Facebook className="h-4 w-4" />
            </a>
          </div>
        </div>

        <div>
          <h4 className="font-display font-bold uppercase tracking-wider text-xs text-yellow-400 mb-4">Navigazione</h4>
          <ul className="space-y-2 text-sm text-zinc-400">
            <li><Link to="/squadra" className="hover:text-zinc-100">Squadra</Link></li>
            <li><Link to="/palestra" className="hover:text-zinc-100">Corsi palestra</Link></li>
            <li><Link to="/news" className="hover:text-zinc-100">News</Link></li>
            <li><Link to="/chi-siamo" className="hover:text-zinc-100">Chi siamo</Link></li>
            <li><Link to="/contatti" className="hover:text-zinc-100">Contatti</Link></li>
         <li><Link to="/privacy" className="hover:text-zinc-100">Privacy Policy</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="font-display font-bold uppercase tracking-wider text-xs text-yellow-400 mb-4">Contatti</h4>
          <ul className="space-y-3 text-sm text-zinc-400">
            {settings.indirizzo && (
              <li className="flex items-start gap-2">
                <MapPin className="h-4 w-4 mt-0.5 text-zinc-500 shrink-0" /> {settings.indirizzo}
              </li>
            )}
            {settings.telefono && (
              <li className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-zinc-500 shrink-0" /> {settings.telefono}
              </li>
            )}
            {settings.email && (
              <li className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-zinc-500 shrink-0" /> {settings.email}
              </li>
            )}
          </ul>
        </div>
      </div>

      <div className="border-t border-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-zinc-500">© {new Date().getFullYear()} {settings.nome_societa || "ASD Rubiana Calcio"}. Tutti i diritti riservati.</p>
          <p className="text-xs text-zinc-600 font-display tracking-[0.2em] uppercase">Forza Rubiana</p>
        </div>
      </div>
    </footer>
  );
}